
import { supabase } from "@/integrations/supabase/client";
import { Mailbox } from "@/lib/types";
import { toast } from "sonner";

/**
 * Fetches all mailboxes for the current tenant
 */
export async function getMailboxes(tenantId: string | null): Promise<Mailbox[]> {
  if (!tenantId) {
    console.error("No tenant ID provided");
    toast.error("Authentication error");
    return [];
  }

  try {
    // Use a JOIN with the rules table to get the count of rules for each mailbox
    const { data, error } = await supabase
      .from('mailboxes')
      .select(`
        *,
        rules_count:rules(count)
      `)
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Transform the results to match the Mailbox interface
    const mailboxes: Mailbox[] = (data || []).map(mailbox => ({
      ...mailbox,
      type: mailbox.type === "shared" ? "shared" : "standard", // Ensure type is either "shared" or "standard"
      status: mailbox.status === "active" ? "active" : "inactive", // Ensure status is either "active" or "inactive"
      rules_count: mailbox.rules_count?.[0]?.count || 0 // Extract count from the nested array
    }));

    return mailboxes;
  } catch (error: unknown) {
    if (error instanceof Error) {
        console.error("Error fetching mailboxes:", error.message);
    } else {
        console.error("Error fetching mailboxes:", error);
    }
    toast.error("Failed to load mailboxes");
    return [];
  }
}

/**
 * Deletes a mailbox
 */
export async function deleteMailbox(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('mailboxes')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    
    toast.success("Mailbox removed successfully");
    return true;
  } catch (error: unknown) {
    if (error instanceof Error) {
        console.error("Error removing mailbox:", error.message);
    } else {
        console.error("Error removing mailbox:", error);
    }
    toast.error("Failed to remove mailbox");
    return false;
  }
}
